import { CornerDownRight } from 'lucide-react'
import { ChangeIndicator } from './ChangeIndicator'
import { connectorFor, funnelFillWidth, funnelMax, funnelSlots, previousTotal } from './funnel'
import styles from './Widgets.module.css'

// The conversion journey as stage rows read top to bottom: the stage's
// label, a bar drawn as its share of the largest stage (so the bars taper
// like a funnel, left edges aligned), the number and how it moved. Between
// two stages a thin connector row, indented to the bars' edge, carries the
// step rate. Rows take the height they need and sit centred in the card.
//
// Steps, one list item each:
//   total: { key, kind: 'total', label, shortLabel?, value, short?,
//            count? (the number the bar is drawn from), change?,
//            rate? (an own property, even null, puts a connector before
//            the row: null reads "Not tracked") }
//   rate:  { key, kind: 'rate', label, shortLabel?, value,
//            state?: 'untracked' | 'updating', note? }
// A narrow card shows the compact number ("23.8K") and the short label
// rather than truncating either; `title` carries the long ones.
export function FunnelChain({ steps, label }) {
  const max = funnelMax(steps)
  return (
    <ol className={styles.funnel} aria-label={label} style={{ '--wf-funnel-slots': funnelSlots(steps), '--wf-funnel-stacked-slots': funnelSlots(steps, { stacked: true }) }}>
      {steps.map((step, index) => {
        const connector = connectorFor(step, previousTotal(steps, index))
        return (
          <li key={step.key} className={styles.funnelStage} data-kind={step.kind} data-state={connector && connector.state !== 'tracked' ? connector.state : undefined}>
            {connector ? <Connector connector={connector} /> : null}
            {step.kind === 'total' ? <Stage step={step} max={max} /> : null}
          </li>
        )
      })}
    </ol>
  )
}

function Connector({ connector }) {
  const short = Boolean(connector.shortLabel && connector.shortLabel !== connector.label)
  return (
    <span className={styles.funnelConnector} title={connector.title} data-short={short ? '' : undefined}>
      <CornerDownRight className={styles.funnelConnectorIcon} aria-hidden="true" />
      <span className={styles.funnelRate}>{connector.value}</span>
      {connector.label ? <span className={styles.funnelRateLabel} data-long="">{connector.label}</span> : null}
      {short ? <span className={`${styles.funnelRateLabel} ${styles.funnelShort}`} aria-hidden="true">{connector.shortLabel}</span> : null}
      {connector.note ? <span className={styles.funnelNote}>{connector.note}</span> : null}
    </span>
  )
}

function Stage({ step, max }) {
  const shortLabel = Boolean(step.shortLabel && step.shortLabel !== step.label)
  const shortValue = Boolean(step.short && step.short !== step.value)
  return (
    <>
      <span className={styles.funnelLabel} title={step.label} data-short={shortLabel ? '' : undefined}>
        <span data-long="">{step.label}</span>
        {shortLabel ? <span className={styles.funnelShort} aria-hidden="true">{step.shortLabel}</span> : null}
      </span>
      <span className={styles.funnelTrack} aria-hidden="true" data-empty={Number.isFinite(step.count) ? undefined : ''}>
        <span className={styles.funnelFill} style={{ width: funnelFillWidth(step.count, max) }} />
      </span>
      <span className={styles.funnelValue} title={step.value} data-short={shortValue ? '' : undefined}>
        <span data-long="">{step.value}</span>
        {shortValue ? <span className={styles.funnelShort} aria-hidden="true">{step.short}</span> : null}
      </span>
      {step.change ? <ChangeIndicator change={step.change} className={styles.funnelChange} size={12} text={step.change.short} title={step.change.label} /> : null}
    </>
  )
}
